// default-state-seed.ts — first-run seed states, extracted from storage.ts.
// createDefaultState() is the DEMO seed (Vietnamese sample content) and must only
// be used for a genuine first run (no persisted data at all). Migration/repair of
// existing data must never fall back to it — see migrateState in storage.ts (F10).

import type { LifeOsState } from "../types";
import { addDaysIso, todayIso } from "./date";

export const DEFAULT_ACCOUNT_ID = "account_default";

export function createDefaultState(): LifeOsState {
  const today = todayIso();

  return {
    tasks: [
      {
        id: "task_today_review",
        title: "Xem lại màn Hôm nay & chọn 3 ưu tiên",
        dueDate: today,
        status: "todo",
        priority: "high",
        projectId: "project_life_os",
        goalId: "goal_operating_system",
        estimateMinutes: 20,
        subtasks: [
          { id: "subtask_today_open", title: "Mở dashboard & lướt lịch hôm nay", done: false },
          { id: "subtask_today_choose", title: "Chọn 3 việc quan trọng nhất", done: false },
        ],
      },
      {
        id: "task_budget",
        title: "Ghi chi tiêu hôm nay trước 21h",
        dueDate: today,
        status: "doing",
        priority: "medium",
        estimateMinutes: 10,
        subtasks: [],
      },
      {
        id: "task_week_plan",
        title: "Lập kế hoạch milestone tuần tới",
        dueDate: addDaysIso(2),
        status: "todo",
        priority: "medium",
        projectId: "project_life_os",
        estimateMinutes: 45,
        subtasks: [],
      },
    ],
    projects: [
      {
        id: "project_life_os",
        name: "Xây Personal Life OS",
        area: "Hệ thống",
        status: "active",
        deadline: addDaysIso(30),
        progress: 34,
        nextAction: "Định nghĩa workflow ngày đầu & yêu cầu đồng bộ",
      },
      {
        id: "project_health_reset",
        name: "Tái thiết sức khoẻ",
        area: "Sức khoẻ",
        status: "active",
        deadline: addDaysIso(60),
        progress: 18,
        nextAction: "Chuẩn bị lịch tập trong tuần",
      },
    ],
    goals: [
      {
        id: "goal_operating_system",
        title: "Vận hành đời sống từ một hệ thống",
        horizon: "quarter",
        metric: "lần review/tuần",
        unit: "lần",
        current: 3,
        target: 12,
        deadline: addDaysIso(90),
        status: "active",
      },
      {
        id: "goal_savings",
        title: "Xây quỹ dự phòng",
        horizon: "year",
        metric: "tiền tiết kiệm",
        unit: "triệu",
        current: 42,
        target: 120,
        deadline: addDaysIso(365),
        status: "active",
      },
    ],
    habits: [
      {
        id: "habit_reading",
        name: "Đọc sách",
        targetPerWeek: 5,
        completions: [today],
        color: "#3b82f6",
      },
      {
        id: "habit_workout",
        name: "Tập thể dục",
        targetPerWeek: 4,
        completions: [],
        color: "#ef4444",
      },
      {
        id: "habit_journal",
        name: "Viết nhật ký",
        targetPerWeek: 7,
        completions: [today],
        color: "#10b981",
      },
    ],
    notes: [
      {
        id: "note_principles",
        title: "Nguyên tắc vận hành",
        tags: ["life-os", "review"],
        body: "Mỗi ngày bắt đầu bằng màn Hôm nay. Capture nhanh, quyết một lần, review hàng tuần.",
        pinned: true,
        attachments: [],
        updatedAt: new Date().toISOString(),
      },
      {
        id: "note_finance",
        title: "Quy tắc tài chính",
        tags: ["tài-chính"],
        body: "Theo dõi dòng tiền hàng tuần. Tách chi thiết yếu / linh hoạt / tiết kiệm.",
        pinned: false,
        attachments: [],
        updatedAt: new Date().toISOString(),
      },
    ],
    transactions: [
      {
        id: "txn_salary",
        date: today,
        type: "income",
        category: "Thưởng",
        amount: 5000000,
        note: "Thưởng dự án",
      },
      {
        id: "txn_food",
        date: today,
        type: "expense",
        category: "Ăn uống",
        amount: 180000,
        note: "Trưa & cà phê",
      },
      {
        id: "txn_tools",
        date: addDaysIso(-1),
        type: "expense",
        category: "Công cụ",
        amount: 360000,
        note: "Phần mềm",
      },
    ],
    budgets: [
      {
        id: "budget_food",
        category: "Ăn uống",
        monthlyLimit: 6000000,
      },
      {
        id: "budget_tools",
        category: "Công cụ",
        monthlyLimit: 1500000,
      },
    ],
    inbox: [
      {
        id: "inbox_daily_capture",
        text: "Ghi lại việc còn dở trước khi review cuối ngày",
        hint: "Hôm nay",
        createdAt: new Date().toISOString(),
      },
    ],
    events: [
      {
        id: "event_weekly_review",
        title: "Review Life OS hàng tuần",
        date: addDaysIso(3),
        time: "09:00",
        color: "#2563eb",
      },
    ],
    recurring: [
      {
        id: "rec_salary",
        type: "income",
        category: "Lương",
        amount: 45000000,
        dayOfMonth: 5,
        note: "Lương tháng",
        startDate: addDaysIso(-90),
      },
      {
        id: "rec_rent",
        type: "expense",
        category: "Tiền nhà",
        amount: 5000000,
        dayOfMonth: 3,
        note: "Tiền thuê nhà",
        startDate: addDaysIso(-90),
      },
    ],
    accounts: [
      {
        id: DEFAULT_ACCOUNT_ID,
        name: "Ví mặc định",
        type: "cash",
        openingBalance: 0,
        currency: "VND",
        createdAt: new Date().toISOString(),
      },
    ],
    dailyPlans: [],
    dailyReviews: [],
    weeklyReviews: [],
    templates: [],
    currencyRates: [],
    financeCategories: [],
    dirtyItems: [],
    deletedItems: [],
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Minimal valid state for "bắt đầu trống" onboarding (P2-12): all collections empty
 * except a single default wallet (so finance still works). Passes migrateState round-trip.
 */
export function createEmptyState(): LifeOsState {
  const now = new Date().toISOString();
  return {
    tasks: [],
    projects: [],
    goals: [],
    habits: [],
    notes: [],
    transactions: [],
    budgets: [],
    inbox: [],
    events: [],
    recurring: [],
    accounts: [
      { id: DEFAULT_ACCOUNT_ID, name: "Ví mặc định", type: "cash", openingBalance: 0, currency: "VND", createdAt: now },
    ],
    dailyPlans: [],
    dailyReviews: [],
    weeklyReviews: [],
    templates: [],
    currencyRates: [],
    financeCategories: [],
    dirtyItems: [],
    deletedItems: [],
    updatedAt: now,
  };
}
